import { useEffect, useRef, useState } from "react";
import { Container } from "./Container";
import { SectionHeading } from "./SectionHeading";
import { ProjectShowcase } from "./ProjectShowcase";
import { ProjectSummaryCard } from "./ProjectSummaryCard";
import { cn } from "../lib/cn";
import { projects } from "../data/portfolio";

/**
 * Every project is listed; the open one expands into its full showcase and the
 * rest stay as key-art-and-title cards.
 */
export function ProjectsSection() {
  const [open, setOpen] = useState(0);
  const cards = useRef<Array<HTMLDivElement | null>>([]);
  // Set when a click should bring the card it opened to the top of the
  // viewport — the cards above it change height, so it has to wait for the
  // re-render before scrolling.
  const [jumpTo, setJumpTo] = useState<number | null>(null);

  useEffect(() => {
    if (jumpTo === null) return;
    cards.current[jumpTo]?.scrollIntoView({ block: "start" });
    setJumpTo(null);
  }, [jumpTo]);

  const show = (index: number) => {
    setOpen(index);
    setJumpTo(index);
  };

  return (
    <section id="projects" className="py-16 md:py-24">
      <Container>
        <SectionHeading
          eyebrow="Work showcase"
          title="Projects with passions, decisions, executions, and fabulous results."
          description="These works reflect my passion, sharpen my thinking, and embody the philosophy I take pride in."
        />

        <nav
          aria-label="Projects"
          className="mt-9 flex flex-wrap justify-center gap-2"
        >
          {projects.map((project, index) => (
            <button
              key={project.title}
              type="button"
              aria-current={index === open ? "true" : undefined}
              onClick={() => show(index)}
              className={cn(
                "ease-standard cursor-pointer rounded-full border px-[1.125rem] py-2.5 text-sm font-medium transition duration-200",
                index === open
                  ? "border-accent-line bg-accent-surface text-strong"
                  : "text-muted border-hairline hover:bg-faint",
              )}
            >
              {project.tabLabel ?? project.title}
            </button>
          ))}
        </nav>

        <div className="mt-8 grid gap-4">
          {projects.map((project, index) => (
            <div
              key={project.title}
              ref={(node) => {
                cards.current[index] = node;
              }}
            >
              {index === open ? (
                <ProjectShowcase project={project} />
              ) : (
                <ProjectSummaryCard
                  project={project}
                  onOpen={() => show(index)}
                />
              )}
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}
